import React from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useNotify, useRecordContext } from "react-admin";

import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { getSum } from "../../helpers/methods";
import { useProfile } from "../DataHooks/useProfile";

export const usePaymentMessage = (order = null, noAutoFill = false) => {
  const { profile } = useProfile();

  const msg = React.useMemo(() => {
    if (profile && order && order.orderNumber) {
      const { paymentTemplate = null } = profile;
      const { orderNumber, orderItems, currencyCode, invoiceNumber } = order;

      let paymentLink = `${process.env.REACT_APP_CUSTOMER_URL}/order/${orderNumber}`;
      if (noAutoFill) {
        paymentLink += "?no_auto_fill=1";
      }

      const newItems = orderItems.filter(({ status }) => status === "new");
      const orderItemsDescription = newItems
        .map((item) => item.description)
        .reduce((acc, cur) => {
          const splitted = cur.split(",");
          return [...acc, ...splitted];
        }, [])
        .join("\n");

      const total = getSum(newItems);
      const orderText = paymentTemplate
        .split("{{currencyCode}}")
        .join(currencyCode)
        .split("{{invoiceNumber}}")
        .join(invoiceNumber)
        .split("{{description}}")
        .join(orderItemsDescription)
        .split("{{total}}")
        .join(total)
        .split("{{paymentLink}}")
        .join(paymentLink);

      return orderText;
    }
    return "";
  }, [profile, order, noAutoFill]);

  return msg;
};

const STORAGE_KEY = "use_no_auto_fill";

const MsgField = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const [noAutoFill, setNoAutoFill] = React.useState(false);

  React.useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      setNoAutoFill(stored === "true");
    }
  }, []);

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(noAutoFill));
  }, [noAutoFill]);

  const msg = usePaymentMessage(record, noAutoFill);

  return (
    <span>
      <Stack spacing={2} alignItems="flex-start">
        <CopyToClipboard
          text={msg}
          onCopy={() => {
            notify("Copied successfully");
          }}
        >
          <Button variant="contained">Copy</Button>
        </CopyToClipboard>
        <FormControlLabel
          control={
            <Checkbox
              checked={noAutoFill}
              onChange={(e) => setNoAutoFill(e.target.checked)}
            />
          }
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2">
                Disable auto-filled customer address?
              </Typography>
              <Tooltip title="Use this for special cases where you don’t want the customer to see an auto-filled address when opening the payment link.">
                <InfoOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            </Stack>
          }
        />
        <Typography
          variant="body3"
          sx={{
            whiteSpace: "pre-wrap",
            p: 1,
            borderRadius: 1,
            border: "1px solid",
            width: "100%",
          }}
        >
          {msg}
        </Typography>
      </Stack>
    </span>
  );
};

export default MsgField;
